import React, { useEffect, useRef, useState } from 'react';
import type { PopupActions } from 'reactjs-popup/dist/types';
import { useTheme } from 'styled-components/macro';

import FlexContainer from 'components/FlexContainer/FlexContainer';
import { getPopupContentStyle } from 'components/UserInfo/UserInfo.styled';
import type { LatestEulaResponseDTO } from 'declarations/dto/eula';
import { ScrollLockIdE } from 'declarations/enums/scroll';
import { StorageE } from 'declarations/enums/storage';
import { getDecodedToken } from 'helpers/jwtToken';

import {
  createNewEulaAgreement,
  getLatestEula,
  getUsersLatestEulaAgreementStatus,
} from '../../api/eula';
import useUserInfo from '../UserInfo/hooks/useUserInfo';

import {
  EulaButtonStyled,
  EulaPopupStyled,
  FlexContainerStyled,
  TypographyContentStyled,
  TypographyTitleStyled,
} from './EulaAgreement.styled';

type EulaAgreementProps = Record<string, never>;

const EulaAgreement: React.FC<EulaAgreementProps> = () => {
  const [showEulaAgreement, setShowEulaAgreement] = useState(false);
  const [eulaContent, setEulaContent] = useState<LatestEulaResponseDTO>({
    id: '',
    version: '',
    releaseDate: '',
    description: '',
  });
  const [loading, setLoading] = useState(false);
  const menuPopupRef = useRef<PopupActions>(null);
  const theme = useTheme();
  const { handleSignOutClick } = useUserInfo();
  const idToken = localStorage.getItem(StorageE.IdToken);
  const decodedToken = getDecodedToken(idToken);
  const userId = (decodedToken?.['custom:user_id'] as string) ?? '';

  useEffect(() => {
    const fetchEulaAccepted = async (): Promise<void> => {
      try {
        setLoading(true);
        if (!userId) {
          return;
        }
        const statusResponse = await getUsersLatestEulaAgreementStatus(userId);
        if (!statusResponse) {
          setEulaContent(await getLatestEula());
        }
        setShowEulaAgreement(!statusResponse);
      } catch (error) {
        // eslint-disable-next-line no-console -- intentional error logging for EULA fetch failure
        console.error(error);
      } finally {
        setLoading(false);
      }
    };
    fetchEulaAccepted();
  }, [userId]);

  const handleAgree = async (): Promise<void> => {
    if (userId) {
      const request = {
        eulaId: eulaContent.id,
        userId: userId,
        accepted: true,
      };
      await createNewEulaAgreement(request);
      setShowEulaAgreement(false);
      window.location.reload();
    } else {
      throw new Error('Missing Id token.');
    }
  };

  const handleDisagree = async (): Promise<void> => {
    if (userId) {
      const request = {
        eulaId: eulaContent.id,
        userId: userId,
        accepted: false,
      };
      await createNewEulaAgreement(request);
      setShowEulaAgreement(false);
      handleSignOutClick();
    } else {
      throw new Error('Missing Id token.');
    }
  };

  const getDate = (dateString: string): string => {
    return new Date(dateString).toLocaleDateString();
  };

  return (
    <>
      {showEulaAgreement && (
        <FlexContainer direction="column" align="center">
          <EulaPopupStyled
            offsetY={4}
            arrow={false}
            ref={menuPopupRef}
            position={'center center'}
            contentStyle={getPopupContentStyle(theme, false)}
            popupId={ScrollLockIdE.EulaPopup}
            isOpen={showEulaAgreement}
            modal={true}
            closeOnEscape={false}
            closeOnDocumentClick={false}
          >
            <FlexContainerStyled direction="column" align="center">
              <TypographyTitleStyled variant="h2" color="dark" align="center">
                End User License Agreement
                <br />
                {loading ? 'Loading...' : 'Released: ' + getDate(eulaContent.releaseDate)}
              </TypographyTitleStyled>
              <TypographyContentStyled
                dangerouslySetInnerHTML={{ __html: eulaContent.description }}
              ></TypographyContentStyled>
              <FlexContainer justify={false ? 'center' : 'flex-end'}>
                <EulaButtonStyled onClick={handleDisagree} variant="outline">
                  Decline
                </EulaButtonStyled>
                <EulaButtonStyled onClick={handleAgree} disabled={loading}>
                  {loading ? 'Loading...' : 'Accept'}
                </EulaButtonStyled>
              </FlexContainer>
            </FlexContainerStyled>
          </EulaPopupStyled>
        </FlexContainer>
      )}
    </>
  );
};

export default EulaAgreement;
